import gql from 'graphql-tag';
import {
  AddressComponentOverride,
  AuditCheckboxComponentOverride,
  AuditTagComponentOverride,
  AuditTextComponentOverride,
  CombinedTagsComponentOverride,
  DateComponentOverride,
  DateRangeComponentOverride,
  EmailComponentOverride,
  EntityBatchComponentOverride,
  FileComponentOverride,
  GeolocationComponentOverride,
  InputButtonComponentOverride,
  NumberComponentOverride,
  PersonComponentOverride,
  ReferencedInComponentOverride,
  RelatedCardComponentOverride,
  RelatedCardLookupComponentOverride,
  ScheduledAutomationComponentOverride,
  SignatureComponentOverride,
  StaticTextComponentOverride,
  SubformComponentOverride,
  TagComponentOverride,
  TextComponentOverride,
  TimeTrackerComponentOverride,
  TodoComponentOverride,
  UrlComponentOverride,
} from './component';

// The leaf one-of selections, factored out so they can be spread at both the top level and
// inside `matchesRelatedEntity` below. GraphQL fragments can't recurse, which is why the nested
// level is spelled out rather than reusing `FilterByComponent` - and one level is exactly the
// depth `matchesRelatedEntity` supports.
export const FilterOneOfFields = gql`
  fragment FilterOneOfFields on FilterOneOfObject {
    stringWildcard {
      value
    }
    containsNumber {
      values
      negate
    }
    containsString {
      values
      negate
    }
    dateIs {
      operator
      relativeDate {
        duration
        timeUnit
      }
    }
    dateRange {
      before
      after
    }
    dateTokenIs {
      value
    }
    numberIs {
      operator
      value
    }
    isEmpty {
      value
    }
    containsCurrentUser {
      value
    }
    booleanIs {
      operator
      value
    }
  }
`;

export const FilterByComponent = gql`
  fragment FilterByComponent on FilterByComponentObject {
    componentId
    scalarKey
    value {
      ...FilterOneOfFields
      matchesRelatedEntity {
        filterByComponent {
          componentId
          scalarKey
          value {
            ...FilterOneOfFields
          }
        }
      }
    }
  }
  ${FilterOneOfFields}
`;

export const CollectionCriteria = gql`
  fragment CollectionCriteria on CollectionCriteriaObject {
    sortByComponents {
      componentId
      direction
    }
    filterByComponents {
      ...FilterByComponent
    }
    groups {
      filterByComponents {
        ...FilterByComponent
      }
    }
  }
  ${FilterByComponent}
`;

export const ViewTemplate = gql`
  fragment ViewTemplate on ViewTemplate {
    id
    createdAt
    updatedAt
    name
    type
    subtype
    customSortingEnabled
    calendarDateComponentId
    calendarHideWeekends
    checklistTagComponentId
    treeParentComponentId
    isPublic
    defaultHiddenComponents
    defaultHiddenLabels
    defaultReadonlyComponents
    displayInCards
    displayLegacyRecurrence
    displayLegacyReminders
    displayLegacyStatusButton
    displayMessageThread
    summarizeReadonlyTags
    sortOrder
    customSortingEnabled
    workflowTemplateId
    collectionCriteria {
      ...CollectionCriteria
    }
    componentsViewOptions {
      componentId
      isHidden
      overrides {
        ...AddressComponentOverride
        ...AuditCheckboxComponentOverride
        ...AuditTagComponentOverride
        ...AuditTextComponentOverride
        ...CombinedTagsComponentOverride
        ...DateComponentOverride
        ...DateRangeComponentOverride
        ...EmailComponentOverride
        ...EntityBatchComponentOverride
        ...FileComponentOverride
        ...GeolocationComponentOverride
        ...InputButtonComponentOverride
        ...NumberComponentOverride
        ...PersonComponentOverride
        ...RelatedCardComponentOverride
        ...RelatedCardLookupComponentOverride
        ...ReferencedInComponentOverride
        ...ScheduledAutomationComponentOverride
        ...SignatureComponentOverride
        ...StaticTextComponentOverride
        ...SubformComponentOverride
        ...TagComponentOverride
        ...TextComponentOverride
        ...TodoComponentOverride
        ...TimeTrackerComponentOverride
        ...UrlComponentOverride
      }
    }
  }
  ${CollectionCriteria}
  ${FilterByComponent}
  ${AddressComponentOverride}
  ${AuditCheckboxComponentOverride}
  ${AuditTagComponentOverride}
  ${AuditTextComponentOverride}
  ${CombinedTagsComponentOverride}
  ${DateComponentOverride}
  ${DateRangeComponentOverride}
  ${EmailComponentOverride}
  ${EntityBatchComponentOverride}
  ${FileComponentOverride}
  ${GeolocationComponentOverride}
  ${InputButtonComponentOverride}
  ${NumberComponentOverride}
  ${PersonComponentOverride}
  ${RelatedCardComponentOverride}
  ${RelatedCardLookupComponentOverride}
  ${ReferencedInComponentOverride}
  ${ScheduledAutomationComponentOverride}
  ${SignatureComponentOverride}
  ${StaticTextComponentOverride}
  ${SubformComponentOverride}
  ${TagComponentOverride}
  ${TextComponentOverride}
  ${TodoComponentOverride}
  ${TimeTrackerComponentOverride}
  ${UrlComponentOverride}
`;

export const ViewTemplateLayoutSection = gql`
  fragment ViewTemplateLayoutSection on ViewTemplateLayoutSection {
    id
    type
    title
    isCollapsed
    componentIds
    columnCount
  }
`;

export const ViewTemplateLayoutComponent = gql`
  fragment ViewTemplateLayoutComponent on ViewTemplateLayoutComponent {
    id
    type
    componentId
  }
`;

export const ViewTemplateLayout = gql`
  fragment ViewTemplateLayout on ViewTemplateLayout {
    id
    items {
      ... on ViewTemplateLayoutSection {
        ...ViewTemplateLayoutSection
      }
      ... on ViewTemplateLayoutComponent {
        ...ViewTemplateLayoutComponent
      }
    }
  }
  ${ViewTemplateLayoutSection}
  ${ViewTemplateLayoutComponent}
`;
